import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaArrowRight,
  FaPlay,
  FaUserFriends,
  FaHistory,
  FaInstagram,
  FaTwitter,
  FaFacebook,
  FaYoutube,
  FaBolt,
  FaStopwatch,
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaMapMarkedAlt,
  FaMoon,
  FaPhoneAlt,
  FaSun,
  FaUsers,
  FaWhatsapp,
  FaShieldAlt,
  FaTrophy,
  FaMedal,
  FaEnvelope,
  FaCheckCircle,
  FaChevronRight,
  FaChevronLeft,
  FaChevronDown,
  FaTimes,
  FaGraduationCap,
  FaShoppingBag,
  FaImages,
  FaUserCircle
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { batchAPI, getImageUrl, productAPI, teacherAPI } from '../services/api';
import logo from '../assets/logo.png';
import heroVideo from '../assets/video.mp4';
import image1 from '../assets/image1.jpeg';
import image2 from '../assets/image2.jpeg';
import image3 from '../assets/image3.jpeg';
import image4 from '../assets/image4.jpeg';
import './HomePage.css';

const THEME_STORAGE_KEY = 'epsilon-home-theme-v2';
const whatsappLink = 'https://wa.me/918871072857?text=Hello%20Epsilon%20Skating%20Club%20-%20I%20would%20like%20to%20inquire%20about%20skating%20classes.';
const youtubeChannelLink = 'https://m.youtube.com/%40Skating_with_OM?fbclid=PAb21jcAQ3d9BleHRuA2FlbQIxMQBzcnRjBmFwcF9pZA81NjcwNjczNDMzNTI0MjcAAaeOwUJdlnIETvZDDM5r1Fvz1ICywSkeYZnQzfGdCA7DEQqupH596an4rHRiuw_aem_iI14NNFzYEmlYaBXhz0FGg';

const sectionVariants = {
  hidden: { opacity: 0, y: 32 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.75,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.12
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] }
  }
};

const programsList = [
  {
    id: 'kids-skating',
    title: 'Kids Skating Classes',
    subtitle: 'Foundation & Joyful Discovery',
    image: image4,
    age: 'Ages 4 - 8',
    level: 'Beginner',
    desc: 'Fun, structured skating sessions designed to help young children develop fundamental balance, leg strength, spatial awareness, and confidence on wheels in a safe environment.',
    features: ['Balance & posture fundamentals', 'Safe falling & stopping techniques', 'Small group coach attention', 'Interactive motor skill games']
  },
  {
    id: 'beginner-training',
    title: 'Beginner Skating Training',
    subtitle: 'Building Strong Fundamentals',
    image: image1,
    age: 'Ages 6+',
    level: 'Beginner to Novice',
    desc: 'Comprehensive entry-level training teaching correct stride mechanics, edge balance, crossovers, speed control, and track safety protocols.',
    features: ['Posture alignment & pushing form', 'Linear & turning edge drills', 'Safety gear & equipment guide', 'Progressive skill milestones']
  },
  {
    id: 'intermediate-training',
    title: 'Intermediate Skating Training',
    subtitle: 'Pacing, Speed & Control',
    image: image2,
    age: 'All Ages',
    level: 'Intermediate',
    desc: 'Designed for skaters ready to transition from casual skating to structured speed development. Focuses on stride power, endurance, lap pacing, and cornering.',
    features: ['Speed generation mechanics', 'Corner leaning & crossover accuracy', 'Stamina building lap sessions', 'Group pacing & draft drills']
  },
  {
    id: 'advanced-training',
    title: 'Advanced Skating Training',
    subtitle: 'Elite Technique & Performance',
    image: image3,
    age: 'Selected Athletes',
    level: 'Advanced',
    desc: 'High-intensity athletic conditioning, explosive acceleration techniques, timing drills, and technical refinements for competitive state-level skaters.',
    features: ['Explosive sprint starts', 'High-speed edge control', 'Interval conditioning & recovery', 'Video motion analysis']
  },
  {
    id: 'speed-skating',
    title: 'Speed Skating Training',
    subtitle: 'Raceway Power & Lap Precision',
    image: image2,
    age: 'Competitive Athletes',
    level: 'Speed / Track',
    desc: 'Specialized track and road speed skating drills aimed at max velocity, sprint strategy, pack positioning, and championship event preparation.',
    features: ['Track corner acceleration', 'Sprint finish mechanics', 'Tactical pack management', 'Time-trial benchmark tests']
  },
  {
    id: 'roller-skating-coaching',
    title: 'Roller Skating Coaching',
    subtitle: 'Versatile Quad & Inline Skills',
    image: image1,
    age: 'All Ages',
    level: 'All Levels',
    desc: 'Dedicated coaching covering both Inline Speed Skates and Quad Skates, tailored to student preferences and regional competitive event categories.',
    features: ['Quad & Inline specialization', 'Equipment tuning & maintenance', 'Agility & slalom coordination', 'Personalized progress logs']
  },
  {
    id: 'competitive-prep',
    title: 'Competitive Skating Preparation',
    subtitle: 'Podium Focus & Championship Mindset',
    image: image3,
    age: 'State & National Level',
    level: 'Championship',
    desc: 'Rigorous preparation module covering mental toughness, race tactics, official competition rules, mock races, and endurance conditioning.',
    features: ['Official event simulation', 'Race psychology & focus', 'Custom competition nutrition', 'Coach tournament mentorship']
  }
];



const fallbackBatches = [
  {
    BatchID: 'fb-1',
    BatchName: 'Junior Stars Batch',
    AboutText: 'Ideal for young children learning balance, safety, stopping, and smooth forward stride on skates.',
    DaysOfWeek: 'Mon, Wed, Fri',
    StartTime: '06:00 AM',
    EndTime: '07:15 AM',
    FeeAmount: 2200,
    DurationValue: 1,
    DurationUnit: 'Month',
    LevelTag: 'Beginner',
    Location: 'Balbir Singh Juneja Indoor Stadium',
    MaxCapacity: 20,
    IsActive: true
  },
  {
    BatchID: 'fb-2',
    BatchName: 'Speed & Edge Intermediate',
    AboutText: 'Focusing on crossovers, lap timing, speed building, and endurance conditioning for regular skaters.',
    DaysOfWeek: 'Tue, Thu, Sat',
    StartTime: '06:30 AM',
    EndTime: '07:45 AM',
    FeeAmount: 3200,
    DurationValue: 1,
    DurationUnit: 'Month',
    LevelTag: 'Intermediate',
    Location: 'Pandri Point (Under Flyover)',
    MaxCapacity: 16,
    IsActive: true
  },
  {
    BatchID: 'fb-3',
    BatchName: 'Championship Speed Squad',
    AboutText: 'High-performance batch for competitive athletes training for state and national speed meets.',
    DaysOfWeek: 'Mon to Sat',
    StartTime: '05:30 AM',
    EndTime: '07:30 AM',
    FeeAmount: 4800,
    DurationValue: 1,
    DurationUnit: 'Month',
    LevelTag: 'Advanced',
    Location: 'Balbir Singh Juneja Indoor Stadium',
    MaxCapacity: 12,
    IsActive: true
  }
];

const fallbackCoaches = [
  {
    TeacherID: 'c-1',
    FirstName: 'Om Prakash',
    LastName: 'Sahu',
    Specialization: 'Head Speed Skating Coach & Founder',
    ExperienceYears: '12+',
    Bio: 'National-level speed skating veteran with over 12 years of coaching excellence in Raipur. Has mentored 350+ athletes to state and national podium finishes.',
    PhotoPath: ''
  },
  {
    TeacherID: 'c-2',
    FirstName: 'Senior Track',
    LastName: 'Coach',
    Specialization: 'Junior & Intermediate Technique Specialist',
    ExperienceYears: '8+',
    Bio: 'Specializes in stride biomechanics, edge control, and building athlete confidence through positive reinforcement and disciplined training routines.',
    PhotoPath: ''
  }
];

const galleryItems = [
  { id: 1, src: image1, category: 'Speed Training', title: 'Team Speed Line Drills', subtitle: 'Balbir Singh Juneja Stadium' },
  { id: 2, src: image2, category: 'Competitions', title: 'Track Sprint Focus', subtitle: 'Raipur District Championship' },
  { id: 3, src: image3, category: 'Endurance', title: 'Outdoor Road Endurance Run', subtitle: 'Morning Speed Session' },
  { id: 4, src: image4, category: 'Junior Academy', title: 'Young Skaters Practice', subtitle: 'Balance & Agility Drills' }
];

const galleryTrackItems = [...galleryItems, ...galleryItems, ...galleryItems, ...galleryItems];

const clubLocations = [
  {
    id: 'stadium',
    title: 'Main Rink Arena',
    name: 'Balbir Singh Juneja Indoor Stadium',
    address: 'Budhapara, Raipur, Chhattisgarh 492001',
    link: 'https://www.google.com/maps/search/?api=1&query=Balbir+Singh+Juneja+Indoor+Stadium+Raipur',
    embedUrl: 'https://maps.google.com/maps?q=Balbir%20Singh%20Juneja%20Indoor%20Stadium%20Raipur&t=&z=15&ie=UTF8&iwloc=&output=embed'
  },
  {
    id: 'pandri',
    title: 'Pandri Practice Track',
    name: 'Under the Flyover, Pandri (Near LIC Building)',
    address: 'Pandri Road, Raipur, Chhattisgarh 492004',
    link: 'https://www.google.com/maps/search/?api=1&query=Under+the+flyover+Pandri+near+LIC+Building+Raipur',
    embedUrl: 'https://maps.google.com/maps?q=Under%20the%20flyover%20Pandri%20near%20LIC%20Building%20Raipur&t=&z=15&ie=UTF8&iwloc=&output=embed'
  }
];

const defaultProducts = [
  {
    ProductID: 8,
    ProductName: "B positive helmet ",
    Category: "Gear",
    Size: "S/M",
    UnitPrice: "1500.00",
    StockQuantity: 30,
    ImagePath: "/uploads/products/products_1776070915794-478100992.jpeg",
    RecordStatus: "Active"
  },
  {
    ProductID: 7,
    ProductName: "Be positive ( CH-1 )",
    Category: "Toy inline ",
    Size: "All size available ",
    UnitPrice: "7500.00",
    StockQuantity: 100,
    ImagePath: "/uploads/products/products_1776070696233-720746854.jpeg",
    RecordStatus: "Active"
  },
  {
    ProductID: 9,
    ProductName: "MINIMIZER Bearing ",
    Category: "Bearing ",
    Size: "",
    UnitPrice: "18000.00",
    StockQuantity: 100,
    ImagePath: "/uploads/products/products_1785752501621-637112796.jpeg",
    RecordStatus: "Active"
  },
  {
    ProductID: 5,
    ProductName: "SR DASH",
    Category: "Professional inline ",
    Size: "All size",
    UnitPrice: "58000.00",
    StockQuantity: 100,
    ImagePath: "/uploads/products/products_1776070535921-377753395.jpeg",
    RecordStatus: "Active"
  },
  {
    ProductID: 6,
    ProductName: "Viva Gold ",
    Category: "Professional skate",
    Size: "All size available ",
    UnitPrice: "25000.00",
    StockQuantity: 100,
    ImagePath: "/uploads/products/products_1776070616878-46987103.jpeg",
    RecordStatus: "Active"
  }
];

function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isStudent, isTeacher } = useAuth();
  const [batches, setBatches] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [products, setProducts] = useState(defaultProducts);
  const [selectedProductCategory, setSelectedProductCategory] = useState('All');
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeProgramIndex, setActiveProgramIndex] = useState(0);
  const [activeBatchFilter, setActiveBatchFilter] = useState('All');
  const [activeMapId, setActiveMapId] = useState('stadium');
  const [selectedGalleryImg, setSelectedGalleryImg] = useState(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  // Footer Accordion Mobile States
  const [footerOpenNav, setFooterOpenNav] = useState(false);
  const [footerOpenPortal, setFooterOpenPortal] = useState(false);
  const [footerOpenVenues, setFooterOpenVenues] = useState(false);

  // Form State for Trial Class / Inquiry
  const [trialFormData, setTrialFormData] = useState({
    parentName: '',
    studentName: '',
    phone: '',
    email: '',
    programInterest: 'Kids Skating Classes',
    message: ''
  });
  const [trialSubmitted, setTrialSubmitted] = useState(false);

  const [theme] = useState('dark');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const loadHomeData = async () => {
      try {
        const [batchRes, coachRes, productRes] = await Promise.all([
          batchAPI.getAll().catch(() => ({ data: [] })),
          teacherAPI.getAll().catch(() => ({ data: [] })),
          productAPI.getAll().catch(() => ({ data: [] }))
        ]);

        const activeBatches = (batchRes.data || []).filter((b) => b.IsActive !== false);
        setBatches(activeBatches.length ? activeBatches : fallbackBatches);

        const loadedCoaches = coachRes.data || [];
        setCoaches(loadedCoaches.length ? loadedCoaches : fallbackCoaches);

        const loadedProducts = productRes.data?.data || productRes.data || [];
        if (Array.isArray(loadedProducts) && loadedProducts.length > 0) {
          setProducts(loadedProducts);
        }
      } catch (err) {
        console.error('Data fetch error:', err);
        setBatches(fallbackBatches);
        setCoaches(fallbackCoaches);
      }
    };
    loadHomeData();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    document.body.classList.remove('home-theme-light', 'home-theme-dark');
    document.body.classList.add(`home-theme-${theme}`);
    document.documentElement.style.colorScheme = theme;
    return () => {
      document.body.classList.remove('home-theme-light', 'home-theme-dark');
      document.documentElement.style.colorScheme = '';
    };
  }, [theme]);



  const handleBuyInquire = (product) => {
    const formattedPrice = Number(product.UnitPrice || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });
    const message = encodeURIComponent(`Hello Epsilon Skating Club! I am interested in purchasing "${product.ProductName}" (Price: ₹${formattedPrice}). Please share availability and order details.`);
    window.open(`https://wa.me/918871072857?text=${message}`, '_blank');
  };

  const handleNavClick = (targetId) => {
    setMenuOpen(false);
    const element = document.getElementById(targetId);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => {
        handleNavClick(location.state.scrollTo);
      }, 150);
    }
  }, [location]);

  const handleProfileClick = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (isTeacher) {
      navigate('/admin/dashboard');
      return;
    }
    navigate('/dashboard');
  };

  const handleJoinClick = () => {
    if (!isAuthenticated) {
      navigate('/register');
      return;
    }
    if (isTeacher) {
      navigate('/admin/dashboard');
      return;
    }
    navigate('/dashboard');
  };

  const handleTrialSubmit = (e) => {
    e.preventDefault();
    setTrialSubmitted(true);
    setTimeout(() => {
      setTrialSubmitted(false);
      setTrialFormData({
        parentName: '',
        studentName: '',
        phone: '',
        email: '',
        programInterest: 'Kids Skating Classes',
        message: ''
      });
    }, 4000);
  };

  const filteredBatches = batches.filter((b) => {
    if (activeBatchFilter === 'All') return true;
    const tag = (b.LevelTag || '').toLowerCase();
    return tag.includes(activeBatchFilter.toLowerCase());
  });

  const productCategories = ['All', ...Array.from(new Set(products.map((p) => (p.Category ? p.Category.trim() : '')).filter(Boolean)))];

  const filteredProducts = selectedProductCategory === 'All'
    ? products
    : products.filter((p) => (p.Category ? p.Category.trim() : '') === selectedProductCategory);

  const productTrackItems = [...filteredProducts, ...filteredProducts, ...filteredProducts, ...filteredProducts];

  const handleProductScroll = (direction) => {
    const track = document.getElementById('product-slider-track');
    if (track) {
      const scrollAmount = direction === 'left' ? -360 : 360;
      track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const selectedMap = clubLocations.find((loc) => loc.id === activeMapId) || clubLocations[0];
  const activeProgram = programsList[activeProgramIndex];

  return (
    <div className={`editorial-home theme-${theme}`}>
      {/* Hidden SEO H1 for exact 1 H1 match */}
      <h1 className="seo-h1-visually-hidden">
        Best Skating Academy in Raipur, Chhattisgarh
      </h1>

      {/* STICKY HEADER */}
      <header className={`editorial-header ${scrolled ? 'header-scrolled' : ''}`}>
        <div className="header-inner">
          <Link to="/" className="brand-badge" aria-label="Epsilon Skating Club Home">
            <div className="brand-logo-shell">
              <img src={logo} alt="Epsilon Skating Club Raipur" className="brand-logo-img" />
            </div>
            <div className="brand-text-block">
              <span className="brand-name">EPSILON</span>
              <span className="brand-sub">SKATING CLUB • RAIPUR</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="desktop-nav" aria-label="Main Navigation">
            <button type="button" onClick={() => handleNavClick('about')} className="nav-item">About</button>
            <button type="button" onClick={() => handleNavClick('programs')} className="nav-item">Programs</button>
            <button type="button" onClick={() => handleNavClick('batches')} className="nav-item">Batches</button>
            <button type="button" onClick={() => handleNavClick('gear-products')} className="nav-item">Products</button>
            <button type="button" onClick={() => handleNavClick('gallery')} className="nav-item">Gallery</button>
            <button type="button" onClick={() => handleNavClick('contact')} className="nav-item">Contact</button>
          </nav>

          {/* Nav Actions */}
          <div className="header-actions">
            <button
              type="button"
              className="profile-nav-btn"
              onClick={handleProfileClick}
              aria-label="User Profile & Login"
              title={isAuthenticated ? "Go to Dashboard" : "Login"}
            >
              <FaUserCircle className="profile-btn-icon" />
              <span className="profile-btn-label">{isAuthenticated ? "Dashboard" : "Login"}</span>
            </button>

            <button
              type="button"
              className={`hamburger-btn ${menuOpen ? 'is-open' : ''}`}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle mobile menu"
            >
              <span className="ham-line ham-line-1" />
              <span className="ham-line ham-line-2" />
            </button>
          </div>
        </div>
      </header>

      {/* MOBILE NAVIGATION DRAWER */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="mobile-drawer-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMenuOpen(false)}
          >
            <motion.div
              className="mobile-drawer-panel"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 220 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="drawer-header">
                <div className="brand-badge">
                  <img src={logo} alt="Logo" className="brand-logo-img" />
                  <span className="brand-name">EPSILON SKATING</span>
                </div>
                <button type="button" className="drawer-close" onClick={() => setMenuOpen(false)}>
                  <FaTimes />
                </button>
              </div>

              <div className="drawer-links">
                <button type="button" onClick={() => handleNavClick('about')}>
                  <FaGraduationCap className="drawer-link-icon" />
                  <span>About Academy</span>
                </button>
                <button type="button" onClick={() => handleNavClick('programs')}>
                  <FaTrophy className="drawer-link-icon" />
                  <span>Training Programs</span>
                </button>
                <button type="button" onClick={() => handleNavClick('batches')}>
                  <FaCalendarAlt className="drawer-link-icon" />
                  <span>Batches & Schedule</span>
                </button>
                <button type="button" onClick={() => navigate('/store')}>
                  <FaShoppingBag className="drawer-link-icon" />
                  <span>Products Store</span>
                </button>
                <button type="button" onClick={() => handleNavClick('gallery')}>
                  <FaImages className="drawer-link-icon" />
                  <span>Visual Gallery</span>
                </button>
                <button type="button" onClick={() => handleNavClick('contact')}>
                  <FaMapMarkerAlt className="drawer-link-icon" />
                  <span>Contact & Map</span>
                </button>
              </div>

              <div className="drawer-ctas">
                <button type="button" className="btn-editorial-primary btn-full" onClick={handleJoinClick}>
                  Join Our Skating Program <FaArrowRight />
                </button>
                <button type="button" className="btn-editorial-secondary btn-full" onClick={() => handleNavClick('contact')}>
                  Book a Free Trial Class
                </button>
              </div>

              <div className="drawer-contact-info">
                <p><FaMapMarkerAlt /> Raipur, Chhattisgarh</p>
                <p><FaPhoneAlt /> +91 8871072857</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <main>
        {/* HERO SECTION */}
        <section className="editorial-hero">
          <div className="hero-video-container">
            <video
              className="hero-background-video"
              src={heroVideo}
              autoPlay
              muted
              loop
              playsInline
            />
            <div className="hero-overlay-dark" />
            <div className="hero-grid-lines" />
          </div>



          <div className="hero-scroll-indicator" onClick={() => handleNavClick('about')}>
            <span className="scroll-text">Explore Academy</span>
            <div className="scroll-line-animated" />
          </div>
        </section>

        {/* RUNNING MARQUEE TICKER BELOW VIDEO */}
        <div className="running-text-strip">
          <div className="marquee-track">
            <span className="marquee-content">
              FASTEST SPEED SKATING ACADEMY IN RAIPUR • BUILD FITNESS, BALANCE & POWER • CHAMPIONSHIP WINNERS • ROLL WITH CONFIDENCE • ROLLER SKATING & INLINE SPEED TRAINING • RAIPUR, CHHATTISGARH •&nbsp;
            </span>
            <span className="marquee-content" aria-hidden="true">
              FASTEST SPEED SKATING ACADEMY IN RAIPUR • BUILD FITNESS, BALANCE & POWER • CHAMPIONSHIP WINNERS • ROLL WITH CONFIDENCE • ROLLER SKATING & INLINE SPEED TRAINING • RAIPUR, CHHATTISGARH •&nbsp;
            </span>
          </div>
        </div>

        {/* PRODUCTS / GEAR STORE SECTION (STATIC GRID WITH MORE BUTTON) */}
        <section id="gear-products" className="editorial-section section-products">
          <div className="section-container">
            <div className="product-section-header-wrapper">
              <motion.div
                className="section-header-editorial margin-bottom-0"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={sectionVariants}
              >
                <h2 className="editorial-heading-lg title-single-line">
                  Pro Speed Skates & <span className="serif-highlight">Equipment Collection.</span>
                </h2>
              </motion.div>
            </div>

            {/* Category Filter Bar */}
            <div className="product-category-filter-bar">
              {productCategories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  className={`product-cat-pill ${selectedProductCategory === cat ? 'is-active' : ''}`}
                  onClick={() => setSelectedProductCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Static Product Grid (On Screen) */}
            <div className="products-static-grid">
              {filteredProducts.map((product) => {
                const imageSrc = product.ImagePath ? getImageUrl(product.ImagePath) : image1;
                const formattedPrice = `₹ ${Number(product.UnitPrice || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

                return (
                  <div key={product.ProductID} className="product-sport-card">
                    <div className="product-img-frame">
                      <img
                        src={imageSrc}
                        alt={product.ProductName}
                        loading="lazy"
                        onError={(e) => { e.target.src = image1; }}
                      />
                    </div>
                    <div className="product-info-block">
                      <h3 className="product-title-sport">{product.ProductName}</h3>
                      <p className="product-price-sport">{formattedPrice}</p>
                      <button
                        type="button"
                        className="btn-product-buy"
                        onClick={() => handleBuyInquire(product)}
                      >
                        <FaWhatsapp className="buy-btn-icon" />
                        <span>Buy Now</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* View All / More Products Button */}
            <div className="product-more-bar">
              <button
                type="button"
                className="btn-editorial-primary btn-more-products"
                onClick={() => navigate('/store')}
              >
                <span>View All Products</span>
                <FaArrowRight className="btn-arrow" />
              </button>
            </div>
          </div>
        </section>

        {/* ABOUT SECTION */}
        <section id="about" className="editorial-section section-about">
          <div className="section-container">
            <motion.div
              className="about-editorial-grid"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={sectionVariants}
            >
              {/* Left Column: Visual Stack */}
              <motion.div className="about-visual-column" variants={itemVariants}>
                <div className="about-image-frame">
                  <img src={image1} alt="Epsilon Skating Club Training in Raipur" className="about-img-main" loading="lazy" />
                </div>
              </motion.div>

              {/* Right Column: Editorial Narrative */}
              <motion.div className="about-text-column" variants={itemVariants}>
                <h2 className="editorial-heading-lg">
                  More Than Skating. <span className="serif-highlight">We Build Confidence.</span>
                </h2>

                <p className="editorial-paragraph">
                  Founded with a passion for grassroots sports development in Chhattisgarh, <strong>Epsilon Skating Club</strong> provides
                  a structured, supportive, and high-energy environment for kids and athletes to master roller skating, inline speed skating,
                  and competitive race techniques.
                </p>

                <p className="editorial-paragraph">
                  We believe that athletic training goes far beyond physical agility. Through systematic balance drills, stride mechanics,
                  and positive encouragement, our students develop lifelong habits of physical fitness, mental resilience, self-discipline, and sportsmanship.
                </p>

                {/* Editorial Pillars List */}
                <div className="about-pillars-grid">
                  <div className="pillar-item">
                    <span className="pillar-index">01</span>
                    <div>
                      <h4>Safe Learning First</h4>
                      <p>Full protective gear protocols and trained coach supervision on every lap.</p>
                    </div>
                  </div>
                  <div className="pillar-item">
                    <span className="pillar-index">02</span>
                    <div>
                      <h4>Technical Precision</h4>
                      <p>Focus on knee bend, balance transfer, edge angles, and smooth power delivery.</p>
                    </div>
                  </div>
                  <div className="pillar-item">
                    <span className="pillar-index">03</span>
                    <div>
                      <h4>Competitive Mindset</h4>
                      <p>Preparing dedicated skaters for district, state, and national tournaments.</p>
                    </div>
                  </div>
                  <div className="pillar-item">
                    <span className="pillar-index">04</span>
                    <div>
                      <h4>Inclusive & Encouraging</h4>
                      <p>Personalized attention for every age group, from 4-year-old beginners to teens.</p>
                    </div>
                  </div>
                </div>

                <div className="about-cta-row">
                  <button type="button" className="btn-editorial-primary" onClick={handleJoinClick}>
                    <span>Enroll Your Child Today</span>
                    <FaArrowRight />
                  </button>
                  <a href={whatsappLink} target="_blank" rel="noreferrer" className="btn-whatsapp-outline">
                    <FaWhatsapp /> Chat on WhatsApp
                  </a>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* PROGRAMS SECTION */}
        <section id="programs" className="editorial-section section-programs">
          <div className="section-container">
            <motion.div
              className="section-header-editorial"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={sectionVariants}
            >

              <h2 className="editorial-heading-lg">
                Skating Classes Designed <span className="serif-highlight">For Every Skill Level.</span>
              </h2>
            </motion.div>

            {/* Editorial Interactive Accordion/Tabs Selector */}
            <motion.div
              className="programs-editorial-layout"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={sectionVariants}
            >
              {/* Left Column: Program Index List */}
              <div className="program-nav-list">
                {programsList.map((prog, idx) => (
                  <button
                    key={prog.id}
                    type="button"
                    className={`program-nav-card ${activeProgramIndex === idx ? 'is-active' : ''}`}
                    onClick={() => setActiveProgramIndex(idx)}
                  >
                    <div className="prog-card-top">
                      <span className="prog-num">0{idx + 1}</span>
                      <span className="prog-level-badge">{prog.level}</span>
                    </div>
                    <h3 className="prog-card-title">{prog.title}</h3>
                    <p className="prog-card-subtitle">{prog.subtitle}</p>
                    <div className="prog-card-arrow">
                      <FaChevronRight />
                    </div>
                  </button>
                ))}
              </div>

              {/* Right Column: Selected Program Feature View */}
              <div className="program-feature-display">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeProgram.id}
                    className="feature-display-inner"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -16 }}
                    transition={{ duration: 0.4 }}
                  >
                    <div className="feature-img-wrapper">
                      <img src={activeProgram.image} alt={activeProgram.title} className="feature-img" />
                      <div className="feature-img-overlay">
                        <span className="feature-tag-pill">{activeProgram.age}</span>
                        <span className="feature-tag-pill highlight">{activeProgram.level}</span>
                      </div>
                    </div>

                    <div className="feature-details">
                      <div className="feature-header-meta">
                        <span className="feature-number">PROGRAM 0{activeProgramIndex + 1}</span>
                        <h3 className="feature-title">{activeProgram.title}</h3>
                        <p className="feature-subtitle">{activeProgram.subtitle}</p>
                      </div>

                      <p className="feature-desc">{activeProgram.desc}</p>

                      <div className="feature-highlights-block">
                        <h4>Key Training Objectives</h4>
                        <ul>
                          {activeProgram.features.map((feat) => (
                            <li key={feat}>
                              <FaCheckCircle className="feat-check-icon" />
                              <span>{feat}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="feature-actions">
                        <button type="button" className="btn-editorial-primary" onClick={handleJoinClick}>
                          <span>Enroll in {activeProgram.title}</span>
                          <FaArrowRight />
                        </button>
                        <button type="button" className="btn-editorial-secondary" onClick={() => handleNavClick('contact')}>
                          Inquire Details
                        </button>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        </section>





        {/* BATCHES & TRAINING SCHEDULE SECTION */}
        <section id="batches" className="editorial-section section-batches">
          <div className="section-container">
            <motion.div
              className="section-header-editorial"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={sectionVariants}
            >

              <h2 className="editorial-heading-lg">
                Training Batches & <span className="serif-highlight">Available Slots.</span>
              </h2>
            </motion.div>

            {/* Filter Buttons */}
            <div className="batch-filter-bar">
              {['All', 'Beginner', 'Intermediate', 'Advanced'].map((lvl) => (
                <button
                  key={lvl}
                  type="button"
                  className={`batch-filter-btn ${activeBatchFilter === lvl ? 'is-active' : ''}`}
                  onClick={() => setActiveBatchFilter(lvl)}
                >
                  {lvl === 'All' ? 'All Batches' : `${lvl} Level`}
                </button>
              ))}
            </div>

            <motion.div
              className="batches-editorial-grid"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={sectionVariants}
            >
              {filteredBatches.map((batch) => (
                <motion.article key={batch.BatchID} className="batch-card-editorial" variants={itemVariants}>
                  <div className="batch-card-top-bar">
                    <span className="batch-level-pill">{batch.LevelTag || 'All Levels'}</span>
                    <span className="batch-fee-tag">₹{Number(batch.FeeAmount || 0).toLocaleString()} / {batch.DurationUnit || 'Month'}</span>
                  </div>

                  <h3 className="batch-name">{batch.BatchName}</h3>
                  <p className="batch-about">{batch.AboutText || 'Structured skating sessions designed for maximum stride growth and track safety.'}</p>

                  <div className="batch-meta-list">
                    <div className="meta-row">
                      <FaCalendarAlt className="meta-icon" />
                      <span><strong>Days:</strong> {batch.DaysOfWeek || 'Mon, Wed, Fri'}</span>
                    </div>
                    <div className="meta-row">
                      <FaClock className="meta-icon" />
                      <span><strong>Time:</strong> {batch.StartTime || '06:00 AM'} - {batch.EndTime || '07:15 AM'}</span>
                    </div>
                    <div className="meta-row">
                      <FaMapMarkerAlt className="meta-icon" />
                      <span><strong>Location:</strong> {batch.Location || 'Balbir Singh Juneja Indoor Stadium'}</span>
                    </div>
                    <div className="meta-row">
                      <FaUsers className="meta-icon" />
                      <span><strong>Capacity:</strong> Max {batch.MaxCapacity || 20} Skaters</span>
                    </div>
                  </div>

                  <div className="batch-card-footer">
                    <button type="button" className="btn-editorial-primary btn-full" onClick={handleJoinClick}>
                      Enroll in Batch <FaArrowRight />
                    </button>
                  </div>
                </motion.article>
              ))}
            </motion.div>
          </div>
        </section>





        {/* GALLERY SECTION */}
        <section id="gallery" className="editorial-section section-gallery">
          <div className="section-container">
            <motion.div
              className="section-header-editorial"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={sectionVariants}
            >

              <h2 className="editorial-heading-lg">
                Life Inside Epsilon <span className="serif-highlight">Skating Academy.</span>
              </h2>
            </motion.div>
          </div>

          {/* CONTINUOUS SLIDING GALLERY */}
          <div className="gallery-slider-outer">
            <div className="gallery-slider-track">
              {galleryTrackItems.map((item, idx) => (
                <div
                  key={`${item.id}-${idx}`}
                  className="gallery-slide-card"
                  onClick={() => setSelectedGalleryImg(item)}
                >
                  <div className="gallery-slide-img-shell">
                    <img src={item.src} alt={item.title} loading="lazy" />
                  </div>
                  <div className="gallery-item-overlay">
                    <span className="gal-cat-badge">{item.category}</span>
                    <h3 className="gal-item-title">{item.title}</h3>
                    <p className="gal-item-sub">{item.subtitle}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CONTACT & LOCAL SEO SECTION */}
        <section id="contact" className="editorial-section section-contact">
          <div className="section-container">
            <motion.div
              className="section-header-editorial"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={sectionVariants}
            >

              <h2 className="editorial-heading-lg">
                Get In Touch & Book <span className="serif-highlight">A Free Trial Class.</span>
              </h2>
            </motion.div>

            <motion.div
              className="contact-editorial-layout"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={sectionVariants}
            >
              {/* Left Column: Contact Cards & Trial Form */}
              <div className="contact-info-col">
                <div className="contact-cards-grid">
                  <a href="tel:8871072857" className="contact-card-item">
                    <span className="contact-icon"><FaPhoneAlt /></span>
                    <div>
                      <strong>Call Us Directly</strong>
                      <p>+91 8871072857</p>
                    </div>
                  </a>

                  <a href="mailto:info@epsilonskatingclub.com" className="contact-card-item">
                    <span className="contact-icon"><FaEnvelope /></span>
                    <div>
                      <strong>Email Support</strong>
                      <p>info@epsilonskatingclub.com</p>
                    </div>
                  </a>

                  <a href={whatsappLink} target="_blank" rel="noreferrer" className="contact-card-item whatsapp-card">
                    <span className="contact-icon"><FaWhatsapp /></span>
                    <div>
                      <strong>WhatsApp Chat</strong>
                      <p>Instant Inquiry Support</p>
                    </div>
                  </a>
                </div>

                {/* Trial Booking Form */}
                <div className="trial-form-card">
                  <h3 className="form-title">Book a Free Trial Class</h3>
                  <p className="form-subtitle">Fill in student details and our coach will confirm your trial slot in Raipur.</p>

                  {trialSubmitted ? (
                    <motion.div
                      className="form-success-box"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                    >
                      <FaCheckCircle className="success-icon" />
                      <h4>Trial Class Requested!</h4>
                      <p>Thank you! Our coach will call you shortly at <strong>{trialFormData.phone}</strong> to confirm your slot.</p>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleTrialSubmit} className="trial-form">
                      <div className="form-row-2">
                        <div className="form-group">
                          <label htmlFor="parentName">Parent Name *</label>
                          <input
                            id="parentName"
                            type="text"
                            required
                            placeholder="e.g. Rajesh Sharma"
                            value={trialFormData.parentName}
                            onChange={(e) => setTrialFormData({ ...trialFormData, parentName: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="studentName">Student Name & Age *</label>
                          <input
                            id="studentName"
                            type="text"
                            required
                            placeholder="e.g. Aarav (7 Yrs)"
                            value={trialFormData.studentName}
                            onChange={(e) => setTrialFormData({ ...trialFormData, studentName: e.target.value })}
                          />
                        </div>
                      </div>

                      <div className="form-row-2">
                        <div className="form-group">
                          <label htmlFor="phone">Phone / WhatsApp Number *</label>
                          <input
                            id="phone"
                            type="tel"
                            required
                            placeholder="e.g. 9876543210"
                            value={trialFormData.phone}
                            onChange={(e) => setTrialFormData({ ...trialFormData, phone: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="programInterest">Interested Program *</label>
                          <select
                            id="programInterest"
                            value={trialFormData.programInterest}
                            onChange={(e) => setTrialFormData({ ...trialFormData, programInterest: e.target.value })}
                          >
                            <option value="Kids Skating Classes">Kids Skating Classes (Ages 4-8)</option>
                            <option value="Beginner Skating Training">Beginner Skating Training</option>
                            <option value="Intermediate Skating Training">Intermediate Speed Training</option>
                            <option value="Speed Skating Training">Advanced Speed Skating</option>
                            <option value="Roller Skating Coaching">Roller Skating Coaching</option>
                          </select>
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="message">Any Specific Requirements / Questions</label>
                        <textarea
                          id="message"
                          rows="3"
                          placeholder="e.g. Preferred batch timing or shoe size..."
                          value={trialFormData.message}
                          onChange={(e) => setTrialFormData({ ...trialFormData, message: e.target.value })}
                        />
                      </div>

                      <button type="submit" className="btn-editorial-primary btn-full">
                        <span>Submit Trial Class Request</span>
                        <FaArrowRight />
                      </button>
                    </form>
                  )}
                </div>
              </div>

              {/* Right Column: Location Map Switcher */}
              <div className="contact-map-col">
                <div className="map-location-tabs">
                  {clubLocations.map((loc) => (
                    <button
                      key={loc.id}
                      type="button"
                      className={`map-tab-btn ${activeMapId === loc.id ? 'is-active' : ''}`}
                      onClick={() => setActiveMapId(loc.id)}
                    >
                      <FaMapMarkerAlt />
                      <span>{loc.title}</span>
                    </button>
                  ))}
                </div>

                <div className="map-frame-wrapper">
                  <iframe
                    title={`${selectedMap.name} Map`}
                    src={selectedMap.embedUrl}
                    loading="lazy"
                    className="map-iframe"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                  <div className="map-info-banner">
                    <h4>{selectedMap.name}</h4>
                    <p>{selectedMap.address}</p>
                    <a href={selectedMap.link} target="_blank" rel="noreferrer" className="map-external-link">
                      Open Directions in Google Maps <FaMapMarkedAlt />
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="editorial-footer">
        <div className="section-container">
          <div className="footer-top-grid">
            <div className="footer-brand-col">
              <div className="brand-badge footer-badge">
                <img src={logo} alt="Logo" className="brand-logo-img" />
                <span className="brand-name">EPSILON SKATING</span>
              </div>
              <p className="footer-tagline">
                Epsilon Skating Club is a premier skating academy in Raipur, Chhattisgarh offering speed skating,
                roller skating, and foundation coaching for kids and competitive athletes.
              </p>
              <div className="footer-social-row">
                <a href="https://instagram.com/epsilon__sports" target="_blank" rel="noreferrer" aria-label="Instagram"><FaInstagram /></a>
                <a href={youtubeChannelLink} target="_blank" rel="noreferrer" aria-label="YouTube"><FaYoutube /></a>
                <a href="https://facebook.com" target="_blank" rel="noreferrer" aria-label="Facebook"><FaFacebook /></a>
                <a href="https://twitter.com" target="_blank" rel="noreferrer" aria-label="Twitter"><FaTwitter /></a>
              </div>
            </div>

            <div className={`footer-nav-col footer-accordion-col ${footerOpenNav ? 'is-open' : ''}`}>
              <h4 onClick={() => setFooterOpenNav(!footerOpenNav)} className="footer-accordion-header">
                <span>Navigation</span>
                <FaChevronDown className="footer-accordion-chevron" />
              </h4>
              <ul className="footer-accordion-content">
                <li><button type="button" onClick={() => handleNavClick('about')}>About Academy</button></li>
                <li><button type="button" onClick={() => navigate('/store')}>Products Store</button></li>
                <li><button type="button" onClick={() => handleNavClick('programs')}>Training Programs</button></li>
                <li><button type="button" onClick={() => handleNavClick('batches')}>Batches Schedule</button></li>
                <li><button type="button" onClick={() => handleNavClick('gallery')}>Visual Gallery</button></li>
                <li><button type="button" onClick={() => handleNavClick('contact')}>Contact & Trial</button></li>
              </ul>
            </div>

            <div className={`footer-portal-col footer-accordion-col ${footerOpenPortal ? 'is-open' : ''}`}>
              <h4 onClick={() => setFooterOpenPortal(!footerOpenPortal)} className="footer-accordion-header">
                <span>Member Portal</span>
                <FaChevronDown className="footer-accordion-chevron" />
              </h4>
              <ul className="footer-accordion-content">
                <li><Link to="/login">Student Login</Link></li>
                <li><Link to="/admin/login">Coach & Admin Login</Link></li>
                <li><Link to="/register">New Student Registration</Link></li>
                <li><a href="tel:8871072857"><FaPhoneAlt /> Call +91 8871072857</a></li>
              </ul>
            </div>

            <div className={`footer-locations-col footer-accordion-col ${footerOpenVenues ? 'is-open' : ''}`}>
              <h4 onClick={() => setFooterOpenVenues(!footerOpenVenues)} className="footer-accordion-header">
                <span>Rink Venues in Raipur</span>
                <FaChevronDown className="footer-accordion-chevron" />
              </h4>
              <div className="footer-accordion-content">
                <p><strong>Main Rink:</strong> Balbir Singh Juneja Indoor Stadium, Budhapara, Raipur, CG</p>
                <p><strong>Outdoor Track:</strong> Under the flyover, Pandri near LIC Building, Raipur, CG</p>
                <p className="timing-note"><strong>Coaching Hours:</strong> Mon - Sat: 6:00 AM - 8:00 PM</p>
              </div>
            </div>
          </div>

          <div className="footer-bottom-bar">
            <p>&copy; {new Date().getFullYear()} Epsilon Skating Club. All Rights Reserved. Raipur, Chhattisgarh, India.</p>
            <p className="footer-seo-keywords">
              Skating Academy in Raipur • Roller Skating Classes in Raipur • Speed Skating Training Chhattisgarh
            </p>
          </div>
        </div>
      </footer>

      {/* FLOATING WHATSAPP CTA */}
      <a href={whatsappLink} target="_blank" rel="noreferrer" className="floating-whatsapp-orbit" aria-label="Contact on WhatsApp">
        <svg viewBox="0 0 160 160" className="whatsapp-orbit-svg" aria-hidden="true">
          <defs>
            <path id="whatsapp-circle-path" d="M 80,80 m -54,0 a 54,54 0 1,1 108,0 a 54,54 0 1,1 -108,0" />
          </defs>
          <text>
            <textPath href="#whatsapp-circle-path" startOffset="0%">
              CONNECT ON WHATSAPP • EPSILON SKATING •
            </textPath>
          </text>
        </svg>
        <div className="whatsapp-core-icon">
          <FaWhatsapp />
        </div>
      </a>

      {/* GALLERY LIGHTBOX MODAL */}
      <AnimatePresence>
        {selectedGalleryImg && (
          <motion.div
            className="lightbox-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedGalleryImg(null)}
          >
            <motion.div
              className="lightbox-content"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button type="button" className="lightbox-close" onClick={() => setSelectedGalleryImg(null)}>
                <FaTimes />
              </button>
              <img src={selectedGalleryImg.src} alt={selectedGalleryImg.title} />
              <div className="lightbox-caption">
                <span className="gal-cat-badge">{selectedGalleryImg.category}</span>
                <h3>{selectedGalleryImg.title}</h3>
                <p>{selectedGalleryImg.subtitle}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* VIDEO REEL MODAL */}
      <AnimatePresence>
        {isVideoModalOpen && (
          <motion.div
            className="lightbox-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsVideoModalOpen(false)}
          >
            <motion.div
              className="video-modal-content"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button type="button" className="lightbox-close" onClick={() => setIsVideoModalOpen(false)}>
                <FaTimes />
              </button>
              <video src={heroVideo} controls autoPlay className="reel-video-player" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default HomePage;
